import { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Alert, AlertDescription } from '../ui/alert';
import { Heart, Mail, Lock, Users, Stethoscope, Activity, Shield, AlertCircle, UserCog } from 'lucide-react';
import { authApi } from '../../lib/api/auth';
import { UserRole } from '../../lib/api/types';
import { toast } from 'sonner';

import { loginUser, createUser } from "./../../cometchat";

type UserRoleType = 'patient' | 'doctor' | 'physiotherapist' | 'coordinator' | 'nurse' | 'physician_assistant';

interface LoginScreenProps {
  onLogin: (role: UserRoleType, name: string) => void;
  onNavigateToSignUp: () => void;
  onNavigateToForgotPassword?: () => void;
}

const roleUsers = {
  patient: { name: 'Ranjith Kumar', email: 'patient@healthkon.com', phone: '+91 98765 43210' },
  doctor: { name: 'Dr. Meera Krishnan', email: 'doctor@healthkon.com', phone: '+91 98765 43211' },
  physiotherapist: { name: 'Suresh Nair', email: 'therapist@healthkon.com', phone: '+91 98765 43212' },
  coordinator: { name: 'Ramesh Kumar', email: 'coordinator@healthkon.com', phone: '+91 98765 43213' },
  nurse: { name: 'Priya Sharma', email: 'nurse@healthkon.com', phone: '+91 98765 43214' },
  physician_assistant: { name: 'Arjun Reddy', email: 'pa@healthkon.com', phone: '+91 98765 43215' },
};

export function LoginScreen({ onLogin, onNavigateToSignUp, onNavigateToForgotPassword }: LoginScreenProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [selectedRole, setSelectedRole] = useState<UserRoleType>('patient');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Clear previous errors
    setError(null);
    
    // Explicit validation check
    if (!email || !password) {
      const errorMsg = 'Please enter email and password';
      setError(errorMsg);
      toast.error(errorMsg);
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await authApi.login({ email, password });
      
      // Map backend role to frontend role type
      const roleMap: Record<string, UserRoleType> = {
        PATIENT: 'patient',
        DOCTOR: 'doctor',
        THERAPIST: 'physiotherapist',
        COORDINATOR: 'coordinator',
        NURSE: 'nurse',
        PHYSICIAN_ASSISTANT: 'physician_assistant',
      };
      
      const frontendRole = roleMap[response.user.role] || 'patient';
      onLogin(frontendRole, response.user.full_name);
      
      /////////  Comet Chat Login ///////////////
      const uid = response.user.id+"-Pulmo"
      try {
        const existingUser = await loginUser(uid);
      } 
      catch (err: any) {
        console.warn("User not found, creating new user…");

        // If login fails, create user
        try {
          await createUser(uid, response.user.full_name);

          // Now login after creating
          const newUser = await loginUser(uid);
        } catch (e) {
          console.error("User registration failed:", e);
        }
      }
      toast.success('Login successful!');
    } catch (error: any) {
      // Extract error message from different error formats
      let errorMessage = 'Login failed. Please try again.';
      
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      // Handle specific error cases
      if (errorMessage.toLowerCase().includes('incorrect') || 
          errorMessage.toLowerCase().includes('invalid credentials') ||
          errorMessage.toLowerCase().includes('wrong password')) {
        errorMessage = 'Invalid email or password. Please check your credentials and try again.';
      } else if (errorMessage.toLowerCase().includes('not found')) {
        errorMessage = 'No account found with this email address.';
      } else if (errorMessage.toLowerCase().includes('cannot connect')) {
        errorMessage = 'Cannot connect to server. Please check your internet connection.';
      }
      
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleQuickLogin = (role: UserRoleType) => {
    const user = roleUsers[role];
    onLogin(role, user.name);
  };

  return (
    <div className="min-h-screen bg-[#D4F0F8]">
      {/* Header */}
      <div className="bg-[#D4F0F8] p-6 flex items-center justify-center">
        <div className="text-center">
          <div className=" flex items-center justify-center mx-auto">
            <img 
              src="assets/icons/icon-512.png" 
              alt="App Icon"
              className="object-contain"
              style={{width:"20%", borderRadius:"50px"}}
            />
          </div>
          <h1 className="text-primary">Anytime – Anywhere – Healthcare</h1>
        </div>
      </div>

      {/* Login Form */}
      <div className="p-6 max-w-md mx-auto">        

        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle className="text-primary text-center">Welcome Back</CardTitle>
            <CardDescription className="text-center">
              Sign in as {selectedRole.charAt(0).toUpperCase() + selectedRole.slice(1)}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* Error Alert */}
            {error && (
              <Alert variant="destructive" className="mb-4">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            
            <form onSubmit={handleEmailLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email Address</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-3 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      if (error) setError(null); // Clear error when user types
                    }}
                    className="pl-10 rounded-xl"
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-3 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="password"
                    type="password"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      if (error) setError(null); // Clear error when user types
                    }}
                    className="pl-10 rounded-xl"
                    required
                  />
                </div>
              </div>

              <div className="flex justify-end">
                <button
                  type="button"
                  className="text-primary hover:underline"
                  onClick={onNavigateToForgotPassword}
                >
                  Forgot Password?
                </button>
              </div>

              <Button
                type="submit"
                className="w-full bg-accent hover:bg-accent/90 rounded-xl h-12"
                disabled={loading}
              >
                {loading ? 'Logging in...' : 'Login'}
              </Button>
            </form>

            <div className="mt-6 text-center space-y-3">
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-border"></div>
                </div>
                <div className="relative flex justify-center">
                  <span className="bg-card px-4 text-muted-foreground">
                    Don't have an account?
                  </span>
                </div>
              </div>

              <Button
                variant="link"
                className="text-primary"
                onClick={onNavigateToSignUp}
              >
                Sign Up
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="mt-8 text-center">
          <p className="text-muted-foreground">
            Powered by Healthkon
          </p>
        </div>
      </div>
    </div>
  );
}